import { useQuery } from "@apollo/client";
import { useState } from "react";

import { MY_PENDING_SURVEYS_QUERY } from "../../graphql/queries/surveyQueries";

const DISMISS_KEY = "survey-banner-dismissed";
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 1 week

function isDismissed(): boolean {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    try {
        const { timestamp } = JSON.parse(raw);
        return Date.now() - timestamp < DISMISS_DURATION_MS;
    } catch {
        return false;
    }
}

function dismiss(): void {
    localStorage.setItem(
        DISMISS_KEY,
        JSON.stringify({ timestamp: Date.now() }),
    );
}

interface PendingSurvey {
    id: string;
    title: string;
    vpReward: number;
}

export function SurveyBanner() {
    const [wasDismissed, setWasDismissed] = useState(() => isDismissed());
    const { data, loading } = useQuery(MY_PENDING_SURVEYS_QUERY, {
        fetchPolicy: "cache-and-network",
    });

    const surveys: PendingSurvey[] = data?.myPendingSurveys ?? [];

    if (loading || surveys.length === 0 || wasDismissed) {
        return null;
    }

    const handleDismiss = () => {
        dismiss();
        setWasDismissed(true);
    };

    const isSingle = surveys.length === 1;
    const survey = surveys[0];

    return (
        <div
            className="fixed right-0 bottom-0 left-0 z-40 border-t border-slate-200 bg-white p-4 shadow-lg dark:border-slate-700 dark:bg-slate-900"
            role="alert"
            aria-live="polite"
        >
            <div className="container mx-auto flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className="shrink-0 text-2xl">📋</span>
                    <div>
                        {isSingle ? (
                            <>
                                <p className="font-semibold text-slate-900 dark:text-slate-100">
                                    New survey: {survey.title}
                                </p>
                                {survey.vpReward > 0 && (
                                    <p className="text-sm text-slate-600 dark:text-slate-400">
                                        Complete it to earn {survey.vpReward}{" "}
                                        VP!
                                    </p>
                                )}
                            </>
                        ) : (
                            <>
                                <p className="font-semibold text-slate-900 dark:text-slate-100">
                                    You have {surveys.length} surveys to
                                    complete
                                </p>
                                <p className="text-sm text-slate-600 dark:text-slate-400">
                                    Share your feedback and earn VP!
                                </p>
                            </>
                        )}
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => {
                            window.location.href = isSingle
                                ? `/dashboard/surveys/${survey.id}`
                                : "/dashboard/surveys";
                        }}
                        className="bg-brand hover:bg-brand/90 rounded-lg px-4 py-2 font-medium text-white transition-colors"
                    >
                        {isSingle ? "Take Survey" : "View Surveys"}
                    </button>
                    <button
                        onClick={handleDismiss}
                        className="rounded-lg border border-slate-300 bg-white px-4 py-2 font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>
    );
}
